import { toastModule } from "@/modules/toast";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import {
  newBlob,
  stopMediaRecorder,
  suspendAudioContext,
} from "@/store/audio-capture/audio-capture-error-handling-helpers";
import { startDeviceChangeWatcher } from "@/store/audio-capture/web/audio/device-change-watcher";
import { startSilenceMonitor } from "@/store/audio-capture/web/audio/silence-monitor";
import { RecordingStatus } from "@/store/audio-capture/models/recording-status";
import type { VoiceAnalyticsTracker } from "@/store/audio-capture/analytics/voice-analytics-tracker";
import type { AppStoreAudioCaptureStore } from "@/store/audio-capture/AppStoreAudioCaptureStore";

import type { RecorderEngine } from "./base-recorder-engine";
import { MicrophoneCaptureService } from "@/store/audio-capture/web/services/microphone-capture-service";

// Web capture is simple enough that the engine itself coordinates recording and
// transcription, so we keep everything inline here rather than introducing a
// separate "Web" orchestrator like the desktop path uses.
export class WebRecorderEngine implements RecorderEngine {
  private stopDeviceWatcherFn: (() => void) | null = null;
  private stopSilenceMonitorFn: (() => void) | null = null;

  constructor(
    private readonly store: AppStoreAudioCaptureStore,
    private readonly analytics: VoiceAnalyticsTracker,
    private readonly microphoneCapture = new MicrophoneCaptureService()
  ) {}

  async start(noteId: string): Promise<void> {
    await this.initializeRecording(noteId);
    this.startRecording();
    this.analytics.sessionStart(this.analyticsPayload());
  }

  async pause(): Promise<boolean> {
    const { status, mediaRecorder, audioContext } = this.store;

    if (status === RecordingStatus.PAUSED) return true;

    try {
      if (!mediaRecorder) {
        toastModule.triggerToast({
          content: "Voice capture is not initialized",
          toastId: this.store.capturedAudioId,
        });
        logger.error({
          message: "[WebRecorderEngine] pause",
          info: { error: "Voice capture is not initialized" },
        });
        this.store.resetRecording();
        return false;
      }

      if (status !== RecordingStatus.RECORDING) {
        toastModule.triggerToast({
          content: "Cannot pause recording when not recording",
          toastId: this.store.capturedAudioId,
        });
        logger.error({
          message: "[WebRecorderEngine] pause",
          info: { error: "Cannot pause recording when not recording" },
        });
        return false;
      }

      stopMediaRecorder(mediaRecorder);
      await suspendAudioContext(audioContext);
      this.store.transcription.flushBuffers();
      this.store.transcription.pause();

      this.store.setStatus(RecordingStatus.PAUSED);
      this.store.setPausedTime(Date.now());
      this.store.clearDurationInterval();

      this.analytics.sessionPause(this.analyticsPayload());
      return true;
    } catch (error) {
      logger.error({
        message: "[WebRecorderEngine] pause",
        info: { error: objectModule.safeErrorAsJson(error as Error) },
      });
      return false;
    }
  }

  async resume(): Promise<boolean> {
    if (this.store.status !== RecordingStatus.PAUSED) {
      toastModule.triggerToast({
        content: "Cannot resume recording when not paused",
        toastId: this.store.capturedAudioId,
      });
      logger.error({
        message: "[WebRecorderEngine] resume",
        info: { error: "Cannot resume recording when not paused" },
      });
      return false;
    }

    const { mediaRecorder } = await this.setupAudio();

    if (this.store.audioContext) {
      await this.store.audioContext.resume();
    }

    mediaRecorder.start(1_000);
    this.store.transcription.resume();
    this.store.transcription.flushBuffers();

    this.store.setStatus(RecordingStatus.RECORDING);
    this.store.incrementTotalPausedTime(Date.now() - this.store.pausedTime);
    this.store.startDurationInterval();
    this.store.isMicrophoneDisconnected = false;

    this.analytics.sessionResume(this.analyticsPayload());
    return true;
  }

  async stop(): Promise<void> {
    const { status } = this.store;
    if (![RecordingStatus.RECORDING, RecordingStatus.PAUSED].includes(status)) {
      return;
    }

    await new Promise<boolean>(resolve => {
      if (!this.store.mediaRecorder) {
        toastModule.triggerToast({
          content: "Voice capture is not initialized",
          toastId: this.store.capturedAudioId,
        });
        logger.error({
          message: "[WebRecorderEngine] stop",
          info: { error: "Voice capture is not initialized" },
        });
        this.store.resetRecording();
        return resolve(false);
      }

      const runOnStopDirectly = this.store.status === RecordingStatus.PAUSED;

      this.store.setStatus(RecordingStatus.STOPPING);

      const onStop = async () => {
        try {
          const audioBlob = newBlob(this.store.audioChunks, {
            type: this.store.audioChunks[0]?.type ?? "audio/webm",
          });
          logger.info({
            message: "[WebRecorderEngine] MediaRecorder stop",
            info: {
              audioChunks: this.store.audioChunks.length,
              audioBlobSize: audioBlob?.size ?? null,
            },
          });

          this.store.transcription.flushBuffers();
          await new Promise(resolveFlush =>
            setTimeout(resolveFlush, this.store.POST_STOP_BUFFER_MS)
          );
          await this.store.transcription.cleanupSockets();

          if (audioBlob) {
            this.store.audioBlob = audioBlob;
            this.store.audioUrl = URL.createObjectURL(audioBlob);
          }
          this.store.clearDurationInterval();
          this.store.transcription.stop();

          resolve(true);
        } catch (error) {
          logger.error({
            message: "[WebRecorderEngine] stop",
            info: { error: objectModule.safeErrorAsJson(error as Error) },
          });
          resolve(false);
        }
      };

      if (runOnStopDirectly) {
        onStop();
      } else if (this.store.mediaRecorder!.state === "inactive") {
        logger.info({
          message: "[WebRecorderEngine] stop",
          info: {
            currentStatus: this.store.status,
            mediaRecorderState: this.store.mediaRecorder!.state,
            error: "MediaRecorder is already inactive",
          },
        });
        resolve(false);
      } else {
        this.store.mediaRecorder!.onstop = onStop;
        stopMediaRecorder(this.store.mediaRecorder!);
      }
    });

    this.analytics.sessionDone(this.analyticsPayload());
  }

  async dispose(): Promise<void> {
    this.stopDeviceWatcherFn?.();
    this.stopDeviceWatcherFn = null;
    this.stopSilenceMonitorFn?.();
    this.stopSilenceMonitorFn = null;
    await this.microphoneCapture.stop();
  }

  private async initializeRecording(noteId: string): Promise<void> {
    if (!this.canStartRecording(noteId)) {
      toastModule.triggerToast({
        content: "Cannot initialize recording when the recorder is not idle",
        toastId: this.store.capturedAudioId,
      });
      logger.error({
        message: "[WebRecorderEngine] initialize",
        info: { error: "Cannot initialize recording when the recorder is not idle" },
      });
      throw new Error(this.store.START_CANCELLED_ERROR_MESSAGE);
    }

    if (!this.store.supportedMimeType) {
      toastModule.triggerToast({
        content: "Browser does not support audio recording. Please use a different browser.",
        toastId: this.store.capturedAudioId,
      });
      logger.error({
        message: "[WebRecorderEngine] initialize",
        info: { error: "Browser does not support audio recording" },
      });
      throw new Error(this.store.START_CANCELLED_ERROR_MESSAGE);
    }

    const clearTranscript = this.store.status !== RecordingStatus.LOCKED;
    this.store.transcription.initializeTranscriptions({ clearTranscript });
    this.store.beginRecordingSession({
      noteId,
      engine: "web",
      isRecordingWithoutTranscription: false,
      clearExisting: clearTranscript,
    });

    this.store.setStatus(RecordingStatus.INITIALIZING);
    this.store.activeNoteId = noteId;
    this.store.isRecordingWithoutTranscription = false;
    this.store.resetAutoStopState();
    this.store.updateFriendlyDebugInfo({ activeNoteId: noteId });

    try {
      await this.setupAudio();

      const now = Date.now();
      this.store.session.setStartTime({ timestamp: now });
      this.store.session.resetTotalPausedTime();
      this.store.session.resetPausedTime();
      this.store.setStatus(RecordingStatus.INITIALIZED);
      this.store.error = null;
      this.store.isMicrophoneDisconnected = false;
      this.store.updateFriendlyDebugInfo();

      this.store.transcription.initializeSockets();
    } catch (error) {
      if (error instanceof DOMException && error.name === "NotSupportedError") {
        await this.store.transcription.cleanupSockets();
        this.store.resetRecording({ preserveLockedStatus: true });
        await this.initializeRecordingWithoutTranscription(noteId);
        return;
      }

      toastModule.triggerToast({
        content: "Failed to initialize recording",
        toastId: this.store.capturedAudioId,
      });
      logger.error({
        message: "[WebRecorderEngine] initialize",
        info: { error: error instanceof Error ? error.message : String(error) },
      });
      this.store.resetRecording({ preserveLockedStatus: true });
      throw new Error(this.store.START_CANCELLED_ERROR_MESSAGE);
    }
  }

  private async initializeRecordingWithoutTranscription(noteId: string): Promise<void> {
    if (!this.canStartRecording(noteId)) {
      toastModule.triggerToast({
        content: "Cannot initialize recording when the recorder is not idle",
        toastId: this.store.capturedAudioId,
      });
      logger.error({
        message: "[WebRecorderEngine] initialize",
        info: { error: "Cannot initialize recording when the recorder is not idle" },
      });
      throw new Error(this.store.START_CANCELLED_ERROR_MESSAGE);
    }

    const clearTranscript = this.store.status !== RecordingStatus.LOCKED;
    this.store.transcription.initializeTranscriptions({ clearTranscript });
    this.store.beginRecordingSession({
      noteId,
      engine: "web",
      isRecordingWithoutTranscription: true,
      clearExisting: clearTranscript,
    });

    this.store.setStatus(RecordingStatus.INITIALIZING);
    this.store.activeNoteId = noteId;
    this.store.isRecordingWithoutTranscription = true;
    this.store.resetAutoStopState();
    this.store.updateFriendlyDebugInfo({
      activeNoteId: noteId,
      isRecordingWithoutTranscription: true,
    });

    try {
      await this.setupAudio();

      const now = Date.now();
      this.store.session.setStartTime({ timestamp: now });
      this.store.session.resetTotalPausedTime();
      this.store.session.resetPausedTime();
      this.store.setStatus(RecordingStatus.INITIALIZED);
      this.store.error = null;
      this.store.isMicrophoneDisconnected = false;
      this.store.updateFriendlyDebugInfo();
    } catch (error) {
      toastModule.triggerToast({
        content: "Failed to initialize recording",
        toastId: this.store.capturedAudioId,
      });
      logger.error({
        message: "[WebRecorderEngine] initializeWithoutTranscription",
        info: { error: error instanceof Error ? error.message : String(error) },
      });
      this.store.resetRecording();
      throw new Error(this.store.START_CANCELLED_ERROR_MESSAGE);
    }
  }

  private startRecording(): void {
    if (!this.store.mediaRecorder || this.store.status !== RecordingStatus.INITIALIZED) {
      toastModule.triggerToast({
        content: "Voice capture is not initialized",
        toastId: this.store.capturedAudioId,
      });
      logger.error({
        message: "[WebRecorderEngine] startRecording",
        info: { error: "Voice capture is not initialized" },
      });
      this.store.resetRecording();
      throw new Error(this.store.START_CANCELLED_ERROR_MESSAGE);
    }

    this.store.mediaRecorder.start(1_000);
    this.store.setStatus(RecordingStatus.RECORDING);
    this.store.startDurationInterval();

    this.store.transcription.start();
  }

  private async setupAudio() {
    const mimeType = this.store.supportedMimeType;
    if (!mimeType) throw new Error("No supported MIME type found");

    const {
      stream: micStream,
      audioContext,
      analyser,
      visualizer,
    } = await this.microphoneCapture.start({
      deviceId: this.store.defaultAudioInputDeviceId,
      sampleRate: this.store.isRecordingWithoutTranscription ? undefined : this.store.SAMPLE_RATE,
    });

    const mediaRecorder = new MediaRecorder(micStream, { mimeType });

    this.store.mediaRecorder = mediaRecorder;
    this.store.audioContext = audioContext;
    this.store.analyser = analyser;
    this.store.visualizer?.dispose();
    this.store.visualizer = visualizer;

    await this.store._detectAudioInputDevices(micStream);
    this.startDeviceChangeHandler(micStream);
    this.startMonitoringSilence(analyser);

    mediaRecorder.ondataavailable = this.store.handleMediaRecorderData;

    if (!this.store.isRecordingWithoutTranscription) {
      const primaryTranscription = this.store.transcriptions[0];
      if (primaryTranscription) {
        await primaryTranscription.setupTranscription?.(micStream);
      }
    }

    return { stream: micStream, mediaRecorder };
  }

  private startDeviceChangeHandler(stream: MediaStream) {
    this.stopDeviceWatcherFn?.();
    this.stopDeviceWatcherFn = startDeviceChangeWatcher({
      getActiveStream: () => stream,
      getRequiredActiveDevices: () => this.store.activeAudioInputDevices,
      onDefaultDeviceId: defaultAudioInputDeviceId => {
        logger.info({
          message: "[WebRecorderEngine] devicechange",
          info: {
            activeAudioInputDeviceIds: this.store.activeAudioInputDevices,
            defaultAudioInputDeviceId: defaultAudioInputDeviceId ?? "not found",
          },
        });
        this.store.defaultAudioInputDeviceId = defaultAudioInputDeviceId;
      },
      onMicrophoneDisconnected: async missingDevice => {
        if (!missingDevice) return;
        this.store.isMicrophoneDisconnected = true;
        logger.warn({
          message: "[WebRecorderEngine] Microphone was disconnected",
          info: { missingDevice },
        });
        if (await this.pause()) {
          await this.resume();
        }
      },
    });
  }

  private startMonitoringSilence(analyser: AnalyserNode) {
    this.stopSilenceMonitorFn?.();
    if (!analyser) return;
    this.stopSilenceMonitorFn = startSilenceMonitor({
      analyser,
      thresholdMs: this.store.SILENCE_DURATION_THRESHOLD_MS,
      onChange: isSilentSince => {
        const isRecording = this.store.status === RecordingStatus.RECORDING;
        this.store.setAudioSilenceSince(isRecording ? isSilentSince : null);
      },
      onSample: sample => {
        this.store.observability.ingestAnalyserSample(sample);
      },
    });
  }

  private canStartRecording(noteId: string) {
    if (this.store.status === RecordingStatus.IDLE) return true;
    if (this.store.status === RecordingStatus.LOCKED && this.store.activeNoteId === noteId)
      return true;
    return false;
  }

  private analyticsPayload() {
    return {
      sessionId: this.store.sessionId,
      capturedAudioId: this.store.capturedAudioId,
      noteId: this.store.activeNoteId,
    };
  }
}
